import { withTransaction } from '@elastic/apm-rum-react'
import {
  DakenService,
  InsideNavigationParcelComponent,
  localForageHelper,
  LogService,
  MenuItemNavigation,
  ModalUnsaveData,
  NavigationLayout,
  onLangChange,
  onUserChange,
  ProfileAdditionalInfo,
  ToastHelper,
  User,
  UserHelper,
} from '@hub/shared'
import { SqCircleCompletation, SqDataLayerHelper } from '@squidit/react-css'
import AddressPage from '@src/pages/profile/pages/address/address.page'
import DataPage from '@src/pages/profile/pages/data/data.page'
import DeletePage from '@src/pages/profile/pages/delete/delete.page'
import NotificationsFormPage from '@src/pages/profile/pages/notifications-form/notifications-form.page'
import PasswordPage from '@src/pages/profile/pages/password/password.page'
import { AxiosResponse } from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'

const ProfilePage = (props: any) => {
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onLangChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const sqDataLayerHelper = useMemo(() => new SqDataLayerHelper(), [])
  const location = useLocation()
  const toast = new ToastHelper().toast
  const userHelper = useMemo(() => new UserHelper(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const logService = useMemo(() => new LogService(), [])
  const [addressCompleteness, setAddressCompleteness] = useState<boolean>(true)
  const [personalCompleteness, setPersonalCompleteness] = useState<boolean>(true)
  const [openModalDocumentInputConfirmation, setOpenModalDocumentInputConfirmation] = useState(false)
  const [hasConfirmDocumentInput, setHasConfirmDocumentInput] = useState(false)
  const [percentageProfileCompleteness, setUserPercentageProfileCompleteness] = useState<number>(0)
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(true)
  const [impersonateObject, setImpersonateObject] = useState<any>(null)
  const [timeOuted, setTimeOuted] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  logService.init('Profile Page')

  const [t] = useTranslation('profileNav')

  const [infoChanged, setInfoChanged] = useState({
    state: false,
    message: '',
  })

  const [validated, setValidated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [infos, setInfos] = useState<ProfileAdditionalInfo>()
  const [menu, setMenu] = useState<Array<MenuItemNavigation>>([
    {
      icon: 'square-user',
      label: t('data'),
      path: '/profile/data',
    },
    {
      icon: 'location-dot',
      label: t('address'),
      path: '/profile/address',
    },
    {
      icon: 'key',
      label: t('password'),
      path: '/profile/password',
    },
    {
      icon: 'list-check',
      label: t('notifications'),
      path: '/profile/notifications',
    },
    {
      icon: 'trash',
      label: t('delete'),
      path: '/profile/delete',
    },
  ])

  useEffect(() => {
    localForageHelper.getItem('impersonateUser').then((impersonateUser) => setImpersonateObject(impersonateUser))
  }, [])

  const findTitle = (path: string) => {
    return menu.find((item) => item.path === path)?.label
  }

  const canUseFooter = () => {
    return !['/profile/delete', '/profile/notifications', '/profile/password'].includes(location.pathname)
  }

  const homeNavigationPath = '/profile'

  const submitConfirmation = useCallback(
    async (messageSucess?: string) => {
      setLoading(true)
      try {
        if (infos?.toUpdateDakenPayload) {
          const response = await dakenService.updateAdditionalInfo(infos)
          if (response?.squidId) {
            sqDataLayerHelper.sendToDataLayer({
              event: 'hub_user_conversion',
              context: 'progress_wheel',
              action: 'update',
              description: 'data_update_wheelcomplete',
              value: percentageProfileCompleteness,
              user: {
                squidId: user?.squidId as string,
              },
            })
            toast.success(messageSucess || t('success'), {
              closeButton: true,
              className: 'full',
              position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
              notOverClick: true,
              immediately: true,
            })
            await userHelper.setUser({ ...user, ...response } as User)
          } else {
            throw new Error('Fail on update user infos')
          }
        }
      } catch (error: any) {
        if (error?.data?.error === 'DK010UnauthorizedEmail') {
          toast.error(t('errors.unauthorizedEmail'), {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
        } else {
          toast.error(t('errors.unexpected'), {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [infos, user, userHelper, dakenService, t, toast, sqDataLayerHelper, percentageProfileCompleteness]
  )

  const getDescription = useCallback(() => {
    switch (location?.pathname) {
      case '/profile/data':
        return 'saveprofile'
      case '/profile/address':
        return 'saveadress'
      case '/profile/password':
        return 'savepassword'
      case '/profile/notifications':
        return 'savenotifications'
      case '/profile/delete':
        return 'deleteprofile'
      default:
        return ''
    }
  }, [location?.pathname])

  const submit = async () => {
    if (!loading && validated) {
      try {
        if (hasConfirmDocumentInput) {
          await submitConfirmation()
        } else if (
          user?.profileadditionalinfo?.documentType === 'CPF' &&
          (infos?.document !== user?.profileadditionalinfo?.document || infos?.nationality !== user?.profileadditionalinfo?.nationality)
        ) {
          setOpenModalDocumentInputConfirmation(true)
        } else if (infos?.email !== user?.profileadditionalinfo?.email) {
          let messageSucess = t('confirmEmailMessage')
          await submitConfirmation(messageSucess)
        } else {
          await submitConfirmation()
        }
        if (!openModalDocumentInputConfirmation) {
          setInfoChanged({ ...infoChanged, state: false, message: '' })
        }

        sqDataLayerHelper.sendToDataLayer({
          event: 'hub_user_interaction',
          context: 'form',
          action: 'submit',
          description: getDescription(),
          user: {
            squidId: user?.squidId as string,
          },
        })

        sqDataLayerHelper?.sendToDataLayer({
          event: 'hub_user_interaction',
          context: 'form',
          action: 'submit',
          description: 'progress_wheel',
          value: percentageProfileCompleteness,
          user: {
            squidId: user?.squidId as string,
          },
        })
      } catch (error: unknown) {
        let errorKey = 'errors.unexpected'

        if ((error as AxiosResponse)?.data?.message === 'Input validation error') {
          errorKey = 'errors.specialCharacters'
        }

        logService.error({
          error,
          message: 'Fail on update user Infos',
        })
        toast.error(t(errorKey), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
        setLoading(false)
      }
    }
  }

  const getValidation = (validated: boolean) => {
    setValidated(validated)
  }

  const getInfo = (infos: ProfileAdditionalInfo) => {
    setInfos(infos)
  }

  const parseLabelKey = (menuItem: MenuItemNavigation) => {
    if (menuItem?.path?.includes('data')) {
      return 'data'
    }
    if (menuItem?.path?.includes('address')) {
      return 'address'
    }
    if (menuItem?.path?.includes('password')) {
      return 'password'
    }
    if (menuItem?.path?.includes('notifications')) {
      return 'notifications'
    }
    if (menuItem?.path?.includes('delete')) {
      return 'delete'
    }
    return ''
  }

  const parseCompleteness = (menuItem: MenuItemNavigation) => {
    if (menuItem?.path?.includes('data')) {
      return 'personal'
    }
    if (menuItem?.path?.includes('address')) {
      return 'address'
    }
    return ''
  }

  useEffect(() => {
    let loadingCompleteness = false
    onUserChangeSubscription.current = onUserChange.subscribe(async (userChanged: User) => {
      if (userChanged?.squidId) {
        setUser(userChanged)
        setInfos(new ProfileAdditionalInfo(userChanged?.profileadditionalinfo))
        if (!loadingCompleteness) {
          try {
            loadingCompleteness = true
            const completeness = await dakenService.getInflucencersCompleteness(userChanged?.squidId, 'profile')
            setUserPercentageProfileCompleteness(completeness?.percentage)
            setPersonalCompleteness(completeness?.personal?.percentage >= 100)
            setAddressCompleteness(completeness?.address?.percentage >= 100)
            setPhoneIsValid(completeness?.personal?.detail?.phoneValid)
            setMenu(
              menu?.map((item) => {
                const key = parseCompleteness(item)
                return {
                  ...item,
                  label: t(parseLabelKey(item)),
                  warning: completeness[key]?.percentage < 100,
                }
              })
            )
          } catch (error) {
            logService.error({
              message: 'Error getting squid',
              error,
            })
          } finally {
            loadingCompleteness = false
          }
        }
      }
    })
    return () => {
      onUserChangeSubscription.current?.unsubscribe()
      onLangChangeSubscription.current?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUserChangeSubscription, dakenService, logService])

  useEffect(() => {
    onLangChangeSubscription.current = onLangChange.subscribe((lang: string) => {
      setMenu((m) =>
        m?.map((item) => {
          return {
            ...item,
            label: t(parseLabelKey(item)),
          }
        })
      )
    })
    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [onLangChangeSubscription, t])

  useEffect(() => {
    if (
      hasConfirmDocumentInput &&
      user?.profileadditionalinfo?.documentType === 'CPF' &&
      infos?.document !== user?.profileadditionalinfo?.document &&
      infos?.nationality !== user?.profileadditionalinfo?.nationality
    ) {
      submitConfirmation()
      setHasConfirmDocumentInput(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConfirmDocumentInput, infos, user])

  const ToHome = () => {
    return window?.innerWidth > 991 ? <Navigate to={'/profile/data'} /> : null
  }

  return (
    <div>
      <NavigationLayout
        isHome={location.pathname === homeNavigationPath}
        menu={menu}
        title={t('title')}
        header={<SqCircleCompletation value={percentageProfileCompleteness} />}
      >
        <InsideNavigationParcelComponent
          homeNavigationPath={homeNavigationPath}
          title={findTitle(location.pathname)}
          footer={canUseFooter()}
          loadingButton={loading}
          disabledButton={!validated || !timeOuted || (!!impersonateObject && !impersonateObject?.impersonateAdmin)}
          confirmFunction={() => submit()}
        >
          <Routes>
            <Route path="/" element={<ToHome />} />
            <Route
              path="/data"
              element={
                <DataPage
                  {...props}
                  user={user}
                  loadingSubmit={loading}
                  getValidation={getValidation}
                  pushFormSubmit={submit}
                  submitConfirmation={submitConfirmation}
                  getInfos={getInfo}
                  hasChanged={(hasChanged: any, message: any) =>
                    setInfoChanged({ ...infoChanged, state: hasChanged || false, message: hasChanged ? message || '' : '' })
                  }
                  registerCompleteness={personalCompleteness}
                  openModalDocumentInputConfirmation={openModalDocumentInputConfirmation}
                  setOpenModalDocumentInputConfirmation={setOpenModalDocumentInputConfirmation}
                  setHasConfirmDocumentInput={setHasConfirmDocumentInput}
                  phoneIsValid={!!props?.configs?._id || phoneIsValid}
                  timeOuted={timeOuted}
                  setTimeOuted={setTimeOuted}
                  impersonateUser={impersonateObject}
                />
              }
            />
            <Route
              path="/address"
              element={
                <AddressPage
                  {...props}
                  impersonateUser={impersonateObject}
                  loadingSubmit={loading}
                  getValidation={getValidation}
                  pushFormSubmit={submit}
                  getInfos={getInfo}
                  hasChanged={(hasChanged: any, message: any) =>
                    setInfoChanged({ ...infoChanged, state: hasChanged || false, message: hasChanged ? message || '' : '' })
                  }
                  addressCompleteness={addressCompleteness}
                />
              }
            />
            <Route
              path="/password"
              element={<PasswordPage {...props} homeNavigationPath={homeNavigationPath} impersonateUser={impersonateObject} />}
            />
            <Route path="/notifications" element={<NotificationsFormPage {...props} impersonateUser={impersonateObject} />} />
            <Route path="/delete" element={<DeletePage {...props} impersonateUser={impersonateObject} />} />
            <Route element={<Navigate to={{ pathname: '/error/not-found' }} />} />
          </Routes>
        </InsideNavigationParcelComponent>
      </NavigationLayout>
      <ModalUnsaveData
        infoChanged={infoChanged}
        disableButton={!!impersonateObject && !impersonateObject?.impersonateAdmin}
        loading={loading}
        onDiscard={() => setInfoChanged({ ...infoChanged, state: false, message: '' })}
        onSave={submit}
      />
    </div>
  )
}

export default withTransaction('ProfilePage', 'component')(ProfilePage)
